import React from 'react'
import { Box, Button, Paper, Typography } from '@mui/material'

import type { CommentReport } from '../../../hooks/socialdash/useSkyReconciliation'
import { addedOnTop, fmtInt } from '../reconciliationModel'

/**
 * What Platinum adds on top (design/2026-10-19-sky-reconciliation-page-redesign.md,
 * M5): Sprinklr counts; Platinum also reads each comment's text and codes it for
 * themes, sentiment and emotion. The coded figure is the server's (`coded` per
 * comment line: the record's current coding completed in a normal pipeline,
 * which is what the dashboards' comment charts count).
 */
export interface AddedByPlatinumProps {
  comments: CommentReport
  onOpenDashboards?: () => void
}

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const

const AddedByPlatinum: React.FC<AddedByPlatinumProps> = ({ comments, onOpenDashboards }) => {
  const a = addedOnTop(comments)
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }} data-testid="recon-added">
      <Typography variant="h6">What Platinum adds on top</Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: '75ch' }}>
        Sprinklr counts. Platinum reads. Of the {fmtInt(a.held)} comments Platinum holds, {fmtInt(a.coded)} have their
        text coded for themes, sentiment and emotion; those are what the dashboards&apos; comment charts show.
        {a.x > 0 && ` X sends no comment text, so its ${fmtInt(a.x)} comments are counted here but not coded.`}
      </Typography>
      {a.byPlatform.length > 0 && (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1.5, display: 'flex', flexWrap: 'wrap', columnGap: 3, rowGap: 0.5 }}>
          {a.byPlatform.map((p) => (
            <Typography component="li" variant="body2" key={p.platform}>
              {p.label} <Box component="span" sx={{ ...num, fontWeight: 600 }}>{fmtInt(p.coded)}</Box> coded
            </Typography>
          ))}
          {a.x > 0 && (
            <Typography component="li" variant="body2" color="text.secondary">
              X <Box component="span" sx={num}>0</Box> coded
            </Typography>
          )}
        </Box>
      )}
      {onOpenDashboards && (
        <Button variant="outlined" color="info" size="small" sx={{ mt: 2 }} onClick={onOpenDashboards}>
          Open the dashboards
        </Button>
      )}
    </Paper>
  )
}

export default AddedByPlatinum
